import React, { useEffect, useState } from 'react';
import { User, Mail, Calendar, LogOut, CheckCircle, XCircle, Activity } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Импортируем наш умный компонент настроек
import { UserProfile, RiskSettings } from '../components/UserProfile';
import { useTrading } from '../hooks/useTrading';

export const ProfilePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const trading = useTrading(true);
  
  // ✅ 1. РАСШИРЯЕМ СОСТОЯНИЕ НОВЫМИ ПОЛЯМИ
  const [riskSettings, setRiskSettings] = useState<RiskSettings>({
    riskPercent: 1,
    leverage: 5,
    stopLoss: 2,
    takeProfit: 6,
    maxDailyLoss: 3,
    minVolume: 1500000,
    maxBalanceUsage: 100,
    maxConcurrentTrades: 3,
    isAdvisor: false,
    ignoreFgi: false,
    telegramChatId: '',
    // --- NEW FEATURES ---
    isDcaEnabled: false,      // По умолчанию выключено
    dcaMultiplier: 1.5,
    dcaStep: 2.0,
    maxDcaOrders: 3,
    isTrailingEnabled: false, // По умолчанию выключено
    trailingCallback: 0.5,
    trailingActivation: 1.0
  });

  const [isLoading, setIsLoading] = useState(true);

  // ✅ 2. СИНХРОНИЗАЦИЯ С СЕРВЕРОМ
  useEffect(() => {
    if (trading.userData) {
      setRiskSettings({
        riskPercent: trading.userData.riskPercent ?? 1,
        leverage: trading.userData.leverage ?? 5,
        stopLoss: trading.userData.stopLoss ?? 2,
        takeProfit: trading.userData.takeProfit ?? 6,
        maxDailyLoss: trading.userData.maxDailyLoss ?? 3,
        minVolume: trading.userData.minVolume ?? 1500000,
        maxBalanceUsage: trading.userData.maxBalanceUsage ?? 100,
        maxConcurrentTrades: trading.userData.maxConcurrentTrades ?? 3,
        isAdvisor: trading.userData.isAdvisor ?? false,
        ignoreFgi: trading.userData.ignoreFgi ?? false,
        telegramChatId: trading.userData.telegramChatId ?? '',
        // --- NEW FEATURES ---
        isDcaEnabled: trading.userData.isDcaEnabled ?? false, // Если нет в базе, будет false
        dcaMultiplier: trading.userData.dcaMultiplier ?? 1.5,
        dcaStep: trading.userData.dcaStep ?? 2.0,
        maxDcaOrders: trading.userData.maxDcaOrders ?? 3,
        isTrailingEnabled: trading.userData.isTrailingEnabled ?? false,
        trailingCallback: trading.userData.trailingCallback ?? 0.5,
        trailingActivation: trading.userData.trailingActivation ?? 1.0
      });
      setIsLoading(false);
    }
  }, [trading.userData]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
    window.location.reload();
  };

  if (isLoading || !trading.userData) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-[#0f172a] text-blue-500">
            <Activity className="animate-spin mr-2" /> {t('common.loading') || 'Loading profile...'}
        </div>
    );
  }

  const user = trading.userData;

  return (
    <div className="max-w-[1600px] mx-auto p-6 md:p-12 animate-in fade-in duration-500">
      
      {/* --- HEADER --- */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">{t('profile.title') || 'My Profile'}</h1>
          <p className="text-slate-400">{t('profile.subtitle') || 'Manage your account settings and trading strategy'}</p>
        </div>
        <button 
            onClick={handleLogout} 
            className="flex items-center gap-2 px-6 py-3 bg-slate-800 hover:bg-red-500/10 hover:text-red-500 hover:border-red-500/50 text-slate-300 rounded-xl transition-all border border-slate-700 shadow-lg group"
        >
          <LogOut size={18} className="group-hover:-translate-x-1 transition-transform" /> 
          {t('profile.logout') || 'Logout'}
        </button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
        
        {/* --- ЛЕВАЯ КОЛОНКА (Инфо о юзере + Баннер) --- */}
        <div className="xl:col-span-4 space-y-8">
          
          {/* USER INFO CARD */}
          <div className="bg-[#1e293b] border border-slate-800 rounded-[32px] p-8 shadow-xl text-center relative overflow-hidden group hover:border-blue-500/30 transition-colors">
              <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-blue-600/20 to-transparent"></div>
              
              <div className="relative z-10 mt-4">
                <div className="w-28 h-28 mx-auto bg-slate-800 rounded-full flex items-center justify-center border-[6px] border-[#1e293b] shadow-2xl mb-6 group-hover:scale-105 transition-transform duration-500 relative">
                   <User size={48} className="text-blue-400" />
                   {/* Status Indicator */}
                   <div className={`absolute bottom-1 right-1 w-6 h-6 rounded-full border-4 border-[#1e293b] ${user.isBotActive ? 'bg-green-500' : 'bg-slate-500'}`}></div>
                </div>
                
                <h2 className="text-2xl font-bold text-white mb-1">{user.email?.split('@')[0] || 'Trader'}</h2>
                <div className="flex flex-wrap items-center justify-center gap-2 mt-3">
                   <span className={`px-4 py-1.5 text-xs font-black rounded-full border uppercase tracking-wider ${user.plan === 'FREE' ? 'bg-slate-700 text-slate-300 border-slate-600' : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-transparent shadow-lg'}`}>
                      {user.plan || 'FREE'} PLAN
                   </span>
                   {user.isPaperTrading && (
                      <span className="px-4 py-1.5 bg-orange-500/10 text-orange-400 text-xs font-black rounded-full border border-orange-500/20 uppercase tracking-wider">
                          PAPER
                      </span>
                   )}
                </div>
              </div>
              
              <div className="mt-10 space-y-3 text-left">
                <div className="flex items-center gap-4 text-sm text-slate-300 p-4 bg-slate-900/50 rounded-2xl border border-slate-800 hover:bg-slate-900 transition-colors">
                   <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400"><Mail size={18}/></div>
                   <div className="flex-1 overflow-hidden">
                      <p className="text-[10px] text-slate-500 font-bold uppercase">Email</p>
                      <p className="truncate">{user.email}</p>
                   </div>
                </div>

                <div className="flex items-center gap-4 text-sm text-slate-300 p-4 bg-slate-900/50 rounded-2xl border border-slate-800 hover:bg-slate-900 transition-colors">
                   <div className="p-2 bg-purple-500/20 rounded-lg text-purple-400"><Calendar size={18}/></div>
                   <div>
                      <p className="text-[10px] text-slate-500 font-bold uppercase">Member Since</p>
                      <p>{new Date(user.createdAt).toLocaleDateString()}</p>
                   </div>
                </div>

                <div className={`flex items-center gap-4 text-sm p-4 bg-slate-900/50 rounded-2xl border border-slate-800 hover:bg-slate-900 transition-colors ${user.telegramChatId ? 'text-green-400' : 'text-slate-400'}`}>
                   <div className={`p-2 rounded-lg ${user.telegramChatId ? 'bg-green-500/20 text-green-400' : 'bg-slate-700 text-slate-400'}`}>
                      {user.telegramChatId ? <CheckCircle size={18} /> : <XCircle size={18}/>}
                   </div>
                   <div>
                      <p className="text-[10px] text-slate-500 font-bold uppercase">Telegram Notifications</p>
                      <p className="font-bold">{user.telegramChatId ? t('profile.connected') : t('profile.notLinked')}</p>
                   </div>
                </div>
              </div>
          </div>
        </div>

        {/* --- ПРАВАЯ КОЛОНКА (UserProfile Component) --- */}
        <div className="xl:col-span-8">
           {/* Рендерим наш функциональный компонент, передавая ему состояние */}
           <UserProfile 
             riskSettings={riskSettings}
             setRiskSettings={setRiskSettings}
             addLog={trading.addLog} // Логи пойдут в общую систему
             userData={user} // Данные для телеграма и ключей
           />
        </div>

      </div>
    </div>
  );
};